import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { formatCryptoAmount, formatCryptoValue, formatUsd } from "../../utils/format";

// Brand colors for recognized assets
const ASSET_COLORS = {
  BTC: "#F7931A",
  ETH: "#6C4CE0",
  SOL: "#B794F6",
  XRP: "#979797",
  USDT: "#26A17B",
  USDC: "#2775CA",
  TRX: "#EF0027",
  BNB: "#F0B90B",
};

const FALLBACK_COLORS = [
  "#F7931A", "#6C4CE0", "#B794F6", "#979797",
  "#26A17B", "#2775CA", "#EF0027", "#F0B90B",
  "#34D399", "#F472B6", "#60A5FA", "#A78BFA",
];

const LegendItem = ({ item }) => (
  <div className="flex min-w-0 items-start gap-1.5 sm:gap-2.5">
    <span
      className="mt-1 size-1.5 shrink-0 rounded-full sm:size-2"
      style={{ backgroundColor: item.color }}
    />
    <div className="min-w-0">
      <p className="whitespace-nowrap text-[10px] font-semibold leading-[15px] text-text-primary sm:text-[12px] sm:leading-[18px]">
        {item.symbol}
      </p>
      <p className="whitespace-nowrap text-[8px] leading-[12px] text-text-secondary sm:text-[9.3px] sm:leading-normal">
        {item.amount} {item.symbol}
      </p>
      {item.hasUsdPrice && (
        <p className="whitespace-nowrap text-[8px] leading-[12px] text-text-secondary sm:text-[9.3px] sm:leading-normal">
          {formatUsd(item.usdValue)}
        </p>
      )}
    </div>
  </div>
);

// Custom Tooltip handling both USD pricing and token balance fallback
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    if (data.isPlaceholder) return null;

    return (
      <div className="rounded-[8px] border border-divider bg-background-surface px-3 py-2 shadow-2xl">
        <p className="text-[11px] font-semibold text-text-primary">
          {data.name} ({data.symbol})
        </p>
        <p className="text-[10px] text-text-secondary">
          {data.amount} {data.symbol}
        </p>
        {data.hasUsdPrice ? (
          <p className="text-[11px] font-bold text-[#4ab932]">
            {formatUsd(data.usdValue)}
          </p>
        ) : (
          <p className="text-[11px] font-bold text-text-primary">
            {data.amount} {data.symbol}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const BalanceChart = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  // 1. Fetch dynamic crypto accounts list from Redux
  const cryptoAccounts = useSelector(
    (state) => state.client?.clientInfo?.crypto_accounts ?? []
  );

  // 2. Format and sort all accounts descending by portfolio valuation / balance
  const legendItems = useMemo(() => {
    if (!cryptoAccounts || cryptoAccounts.length === 0) return [];

    return cryptoAccounts.map((account, index) => {
        const symbol = account?.currency_code ?? "CRYPTO";
        const balance = formatCryptoValue(account?.balance, account?.decimals || 8);
        const unitPrice = Number(account?.unit_price_usd ?? 0);
        const hasUsdPrice = Boolean(account?.unit_price_usd && unitPrice > 0);
        const usdValue = hasUsdPrice ? balance * unitPrice : 0;

        // Use USD value if available and valid; otherwise fallback to the raw balance amount
        const chartValue = hasUsdPrice && usdValue > 0 ? usdValue : balance;

        return {
          id: account.id ?? index,
          name: account.currency_name ?? symbol,
          symbol: symbol,
          amount: balance,
          usdValue: usdValue,
          hasUsdPrice: hasUsdPrice,
          chartValue: chartValue,
          color:
            ASSET_COLORS[symbol.toUpperCase()] ??
            FALLBACK_COLORS[index % FALLBACK_COLORS.length],
        };
      })
      // Sort descending: highest balance/value first, zero balances last
      // .sort((a, b) => b.chartValue - a.chartValue);
      .sort((a, b) => b.amount - a.amount);
  }, [cryptoAccounts]);

  // Check if at least one asset has a valid USD valuation
  const hasAnyUsdValue = useMemo(() => {
    return legendItems.some((item) => item.hasUsdPrice && item.usdValue > 0);
  }, [legendItems]);

  // 3. Compute total portfolio value
  const totalUsd = useMemo(() => {
    return legendItems.reduce((sum, item) => sum + item.usdValue, 0);
  }, [legendItems]);

  const totalBalanceCount = useMemo(() => {
    return legendItems.reduce((sum, item) => sum + item.amount, 0);
  }, [legendItems]);

  // 4. Prepare data for the SVG Pie chart (inherits sorted order from legendItems)
  const chartData = useMemo(() => {
    const activeAssets = legendItems.filter((item) => item.chartValue > 0);

    if (activeAssets.length === 0) {
      return [
        {
          name: "Empty",
          symbol: "USD",
          chartValue: 1,
          amount: 0,
          isPlaceholder: true,
        },
      ];
    }
    return activeAssets;
  }, [legendItems]);

  const isEmpty = chartData[0]?.isPlaceholder;

  // Keep the top four assets on the left at most, with the remaining assets
  // on the right, matching the reference layout.
  const halfIndex =
    legendItems.length > 4
      ? Math.min(4, legendItems.length - 2)
      : Math.ceil(legendItems.length / 2);
  const leftLegend = legendItems.slice(0, halfIndex);
  const rightLegend = legendItems.slice(halfIndex);

  // Dynamic label for center text
  const formattedCenterStr = useMemo(() => {
    if (isEmpty) return "$0.00";
    if (hasAnyUsdValue) return formatUsd(totalUsd);
    return `${formatCryptoAmount(totalBalanceCount)}`;
  }, [isEmpty, hasAnyUsdValue, totalUsd, totalBalanceCount]);

  // Dynamic font scaling to guarantee center text never overflows
  const getCenterFontSize = (textStrLength) => {
    if (textStrLength > 17) return "text-[10px] sm:text-[11px] tracking-tighter";
    if (textStrLength > 14) return "text-[11px] sm:text-[12px] tracking-tight";
    if (textStrLength > 13) return "text-[13px] sm:text-[15px]";
    if (textStrLength > 11) return "text-[13px] sm:text-[14px] tracking-tight";
    if (textStrLength > 10) return "text-[15px] sm:text-[17px]";
    if (textStrLength > 8) return "text-[15px] sm:text-[16px]";
    return "text-[18px] sm:text-[20px]";
  };

  return (
    <div className="w-full font-outfit">
      <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-text-secondary">
        Portfolio breakdown
      </p>

      <div className="mt-4 flex items-center justify-between sm:gap-1 gap-6 sm:mr-4 sm:gap-x-10">
        {/* Left Legend Column (Top balances) */}
        <div className="flex min-w-0 flex-1 self-start flex-col items-start gap-3 sm:gap-3.5">
          {leftLegend.map((item) => (
            <LegendItem key={item.id} item={item} />
          ))}
        </div>

        {/* Center Donut Ring Container */}
        <div className="relative h-[132px] w-[132px] shrink-0 sm:h-[180px] sm:w-[180px]">
          {/* Center Balance Text */}
          <div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center p-2 text-center">
            <span
              className={`font-bold leading-none text-text-primary ${getCenterFontSize(
                formattedCenterStr.length
              )}`}
            >
              {formattedCenterStr}
            </span>
          </div>

          {/* Recharts PieChart */}
          <ResponsiveContainer width="100%" height="100%" className="relative z-0">
            <PieChart>
              {!isEmpty && (
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ zIndex: 9999, outline: "none" }}
                />
              )}
              <Pie
                data={chartData}
                dataKey="chartValue"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="68%"
                outerRadius="88%"
                paddingAngle={!isEmpty && chartData.length > 1 ? 4 : 0}
                cornerRadius={8}
                stroke="none"
                onMouseEnter={(_, index) => !isEmpty && setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.isPlaceholder ? undefined : entry.color}
                    opacity={
                      isEmpty || activeIndex === null || activeIndex === index
                        ? 1
                        : 0.4
                    }
                    className={`custom-transition outline-none ${
                      entry.isPlaceholder
                        ? "cursor-default fill-[#e8e8e8] dark:fill-[#B1BAAD]"
                        : "cursor-pointer"
                    }`}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Right Legend Column (Lower balances) */}
        <div className="flex min-w-0 flex-1 self-start flex-col items-start gap-3 sm:gap-3.5">
          {rightLegend.map((item) => (
            <LegendItem key={item.id} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BalanceChart;